import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
	CalendarDaysIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline'
import { Label } from './FilterFields'

const isoFromDate = d =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const dateFromIso = iso => {
	if (!iso) return null
	const [y, m, d] = iso.split('-').map(Number)
	return new Date(y, m - 1, d)
}

const cmpDate = (a, b) =>
	a.getFullYear() - b.getFullYear() || a.getMonth() - b.getMonth() || a.getDate() - b.getDate()

const fmtShortDate = iso => {
	if (!iso) return null
	const d = dateFromIso(iso)
	if (!d) return null
	const day = d.toLocaleDateString('en-US', { weekday: 'short' })
	return `${day} ${d.getMonth() + 1}/${d.getDate()}`
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
]

const buildMonthGrid = (year, month) => {
	const firstDay = new Date(year, month, 1)
	const start = new Date(year, month, 1 - firstDay.getDay())
	const days = []
	for (let i = 0; i < 42; i++) {
		const d = new Date(start)
		d.setDate(start.getDate() + i)
		days.push(d)
	}
	return days
}

function MonthGrid({
	year,
	month,
	today,
	fromDate,
	toDate,
	hover,
	onHover,
	onClick,
	isLight,
	tokens,
}) {
	const days = useMemo(() => buildMonthGrid(year, month), [year, month])

	const cellBase = 'w-9 h-9 flex items-center justify-center text-[13px] select-none relative z-10'

	const computeCell = d => {
		const isPast = cmpDate(d, today) < 0
		const isToday = cmpDate(d, today) === 0
		const inCurrentMonth = d.getMonth() === month
		const isFrom = fromDate && cmpDate(d, fromDate) === 0
		const isTo = toDate && cmpDate(d, toDate) === 0
		let inRange = false
		if (fromDate && toDate) {
			inRange = cmpDate(d, fromDate) > 0 && cmpDate(d, toDate) < 0
		} else if (fromDate && hover && cmpDate(hover, fromDate) > 0) {
			inRange = cmpDate(d, fromDate) > 0 && cmpDate(d, hover) < 0
		}
		return { isPast, isToday, inCurrentMonth, isFrom, isTo, inRange }
	}

	const rangeBand = isLight ? 'bg-sky-100' : 'bg-sky-500/20'

	return (
		<div className="w-[252px]">
			<div className={`text-center text-sm font-semibold mb-2 ${tokens.valueStrong}`}>
				{MONTH_NAMES[month]} {year}
			</div>
			<div className="grid grid-cols-7 mb-1">
				{WEEKDAYS.map((d, i) => (
					<div key={i} className={`text-[11px] text-center ${tokens.valueMuted}`}>
						{d}
					</div>
				))}
			</div>
			<div className="grid grid-cols-7">
				{days.map((d, i) => {
					const { isPast, isToday, inCurrentMonth, isFrom, isTo, inRange } = computeCell(d)
					const disabled = isPast || !inCurrentMonth

					// The cell is layered: a colored "band" behind, the day number
					// on top in its own pill. This lets the in-range band touch the
					// neighboring cells while the endpoint sits as a clean circle.
					const showLeftHalfBand = (isTo && fromDate) || (inRange && !isFrom)
					const showRightHalfBand =
						(isFrom && (toDate || (hover && cmpDate(hover, fromDate) > 0))) || (inRange && !isTo)

					return (
						<div key={i} className="relative h-9 w-9 flex items-center justify-center">
							{showLeftHalfBand && inCurrentMonth && (
								<div className={`absolute inset-y-1 left-0 w-1/2 ${rangeBand}`} />
							)}
							{showRightHalfBand && inCurrentMonth && (
								<div className={`absolute inset-y-1 right-0 w-1/2 ${rangeBand}`} />
							)}
							{inRange && !isFrom && !isTo && inCurrentMonth && (
								<div className={`absolute inset-y-1 inset-x-0 ${rangeBand}`} />
							)}

							<button
								type="button"
								disabled={disabled}
								onClick={() => onClick(d)}
								onMouseEnter={() => onHover(d)}
								onMouseLeave={() => onHover(null)}
								className={
									cellBase +
									' ' +
									(disabled
										? `${isLight ? 'text-gray-300' : 'text-gray-600'} cursor-not-allowed`
										: isFrom || isTo
											? `rounded-full ${isLight ? 'bg-gray-900 text-white' : 'bg-sky-500 text-white'} cursor-pointer font-semibold`
											: `rounded-full cursor-pointer ${tokens.valueStrong} ${isLight ? 'hover:bg-gray-200' : 'hover:bg-white/10'}`)
								}
							>
								{!disabled && isToday && !(isFrom || isTo) ? (
									<span
										className={`flex items-center justify-center w-7 h-7 rounded-full ring-1 ${isLight ? 'ring-gray-900' : 'ring-sky-400'}`}
									>
										{d.getDate()}
									</span>
								) : (
									d.getDate()
								)}
							</button>
						</div>
					)
				})}
			</div>
		</div>
	)
}

// Airbnb-style range calendar: two months in one popover, click-from →
// click-to selection, hover preview, past dates disabled.
function PickDateRangeField({ from, to, onChange, tokens, inputBase, isLight }) {
	const [isOpen, setIsOpen] = useState(false)
	const wrapRef = useRef(null)
	const today = useMemo(() => {
		const d = new Date()
		d.setHours(0, 0, 0, 0)
		return d
	}, [])

	// Draft range — local edits while the popover is open. Only committed
	// to the parent (and thus the load query) when the popover closes, so
	// the user can pick start + end without firing a fetch in between.
	const [draftFrom, setDraftFrom] = useState(from || '')
	const [draftTo, setDraftTo] = useState(to || '')

	useEffect(() => {
		if (!isOpen) {
			setDraftFrom(from || '')
			setDraftTo(to || '')
		}
	}, [from, to, isOpen])

	const fromDate = useMemo(() => dateFromIso(draftFrom), [draftFrom])
	const toDate = useMemo(() => dateFromIso(draftTo), [draftTo])

	const seed = fromDate || today
	const [leftYear, setLeftYear] = useState(seed.getFullYear())
	const [leftMonth, setLeftMonth] = useState(seed.getMonth())
	const [hover, setHover] = useState(null)

	const commitDraft = () => {
		if (draftFrom !== (from || '') || draftTo !== (to || '')) {
			onChange({ pickDateFrom: draftFrom, pickDateTo: draftTo })
		}
	}

	useEffect(() => {
		if (!isOpen) return
		const onClick = e => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				commitDraft()
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', onClick)
		return () => document.removeEventListener('mousedown', onClick)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, draftFrom, draftTo, from, to])

	const handlePrev = () => {
		if (leftMonth === 0) {
			setLeftYear(y => y - 1)
			setLeftMonth(11)
		} else setLeftMonth(m => m - 1)
	}
	const handleNext = () => {
		if (leftMonth === 11) {
			setLeftYear(y => y + 1)
			setLeftMonth(0)
		} else setLeftMonth(m => m + 1)
	}
	const canGoBack =
		leftYear > today.getFullYear() ||
		(leftYear === today.getFullYear() && leftMonth > today.getMonth())

	const handleDayClick = d => {
		if (cmpDate(d, today) < 0) return
		const iso = isoFromDate(d)
		if (!fromDate || (fromDate && toDate)) {
			setDraftFrom(iso)
			setDraftTo('')
			return
		}
		if (cmpDate(d, fromDate) < 0) {
			setDraftFrom(iso)
			setDraftTo('')
		} else {
			setDraftTo(iso)
		}
	}

	const triggerLabel = (() => {
		const f = fmtShortDate(draftFrom)
		const t = fmtShortDate(draftTo)
		if (!f && !t) return 'Any date'
		if (f && !t) return f
		if (!f && t) return t
		return `${f} – ${t}`
	})()

	const navBtnCls = `p-1.5 rounded-full ${isLight ? 'hover:bg-gray-100' : 'hover:bg-white/10'} disabled:opacity-30 disabled:cursor-not-allowed`

	return (
		<div ref={wrapRef} className="w-[210px] relative">
			<div className={tokens.fieldShell}>
				<Label>Pick Date Range</Label>
				<button
					type="button"
					onClick={() => {
						if (isOpen) {
							commitDraft()
							setIsOpen(false)
						} else {
							setIsOpen(true)
						}
					}}
					className={`${inputBase} group cursor-pointer flex items-center gap-1.5 text-left tracking-wide min-h-[24px] leading-5`}
				>
					<CalendarDaysIcon className="h-4 w-4 text-gray-500 shrink-0" />
					<span className="truncate flex-1">{triggerLabel}</span>
					{(draftFrom || draftTo) && (
						<span
							role="button"
							tabIndex={0}
							aria-label="Clear dates"
							onClick={e => {
								e.stopPropagation()
								setDraftFrom('')
								setDraftTo('')
								onChange({ pickDateFrom: '', pickDateTo: '' })
							}}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.stopPropagation()
									e.preventDefault()
									setDraftFrom('')
									setDraftTo('')
									onChange({ pickDateFrom: '', pickDateTo: '' })
								}
							}}
							className={`hidden group-hover:inline-flex p-0.5 rounded ${isLight ? 'hover:bg-gray-200' : 'hover:bg-white/10'} text-gray-500 hover:text-gray-700`}
						>
							<XMarkIcon className="h-3.5 w-3.5" />
						</span>
					)}
				</button>
			</div>
			{isOpen && (
				<div
					className={`absolute right-0 top-full mt-2 z-30 rounded-xl p-5 shadow-xl ${tokens.dropdownBg}`}
				>
					<div className="flex items-center justify-between mb-3">
						<button
							type="button"
							onClick={handlePrev}
							disabled={!canGoBack}
							className={navBtnCls}
							aria-label="Previous month"
						>
							<ChevronLeftIcon className="h-4 w-4" />
						</button>
						<div className="flex-1" />
						<button
							type="button"
							onClick={handleNext}
							className={navBtnCls}
							aria-label="Next month"
						>
							<ChevronRightIcon className="h-4 w-4" />
						</button>
					</div>
					<div className="-mt-7">
						<MonthGrid
							year={leftYear}
							month={leftMonth}
							today={today}
							fromDate={fromDate}
							toDate={toDate}
							hover={hover}
							onHover={setHover}
							onClick={handleDayClick}
							isLight={isLight}
							tokens={tokens}
						/>
					</div>
					{(draftFrom || draftTo) && (
						<div className="mt-3 flex justify-end">
							<button
								type="button"
								onClick={() => {
									setDraftFrom('')
									setDraftTo('')
								}}
								className={`text-[12px] underline ${tokens.valueMuted}`}
							>
								Clear dates
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	)
}

export default PickDateRangeField
